import React, { useMemo, useState } from "react";

import { ArrowLeft, ArrowRight, Globe2, SquarePlay } from "lucide-react";

import SynopsisLegendInfo from "./SynopsisLegendInfo";
import SynopsisSectionCard from "./SynopsisSectionCard";
import XFactorAccountBriefing from "./XFactorAccountBriefing";
import type { XFactorBriefingView } from "./XFactorAccountBriefing";
import XFactorSectionDots from "./XFactorSectionDots";
import { SectionDetailModal } from "../../../Container/WatchlistSummary";
import type { PublicSectionDot } from "../../../Hooks/useAccountSynopsisData";
import useXFactorAccountBriefing from "../../../Hooks/useXFactorAccountBriefing";
import type { WatchlistSummaryAccount } from "../../../Types/WatchlistSummary";
import { formatIsoDateMonDayYear } from "../../../Utils/Helper/Helper";
import { Modal } from "../../TempViewsRendering/components/Chrome";
import { ExecutiveBriefSectionNav } from "../../TempViewsRendering/components/ExecutiveBrief";

/** Always On Channel Attention tab this card deep-links into. */
const CHANNEL_LINK_LABEL = "Business Health";
/* development renamed this section from "Public Data Briefing" to
   "Business Health"; taking their title. */
const PUBLIC_DATA_SECTION_TITLE = "Business Health";
/** Header control that swaps this section for the xfactor Situation Card. */
const SITUATION_CARDS_LABEL = "Situation Card";

type PublicDataSectionProps = {
  dots: PublicSectionDot[];
  synopsisText: string;
  generatedDate: string | null;
  loading: boolean;
  onOpenChannel: () => void;
  briefingAccount: WatchlistSummaryAccount | null;
  organizationName?: string | null;
  /** EB customer id, used to resolve this account's xfactor briefing. */
  accountId?: string;
};

const PublicDataSection: React.FC<PublicDataSectionProps> = ({
  dots,
  synopsisText,
  generatedDate,
  loading,
  onOpenChannel,
  briefingAccount,
  organizationName,
  accountId,
}) => {
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null
  );
  /**
   * The header's "Situation Cards" control opens the xfactor Situation Card in
   * a modal. Kept on this page rather than routing away: the card belongs to
   * this account, and leaving would lose the surrounding synopsis context.
   */
  const [showSituationCard, setShowSituationCard] = useState(false);
  const [briefingView, setBriefingView] =
    useState<XFactorBriefingView>("situation");
  const { latest } = useXFactorAccountBriefing(accountId);
  const modalDate =
    formatIsoDateMonDayYear(latest?.digest_date || generatedDate) ||
    latest?.digest_date ||
    generatedDate ||
    undefined;

  /** Same as AO Channel: overall is index -1; allSections excludes overall. */
  const navigableSectionIds = useMemo(
    () => dots.filter(dot => dot.id !== "overall").map(dot => dot.id),
    [dots]
  );

  const currentSectionIndex = useMemo(() => {
    if (!selectedSectionId || selectedSectionId === "overall") return -1;
    const idx = navigableSectionIds.indexOf(selectedSectionId);
    return idx >= 0 ? idx : -1;
  }, [selectedSectionId, navigableSectionIds]);

  const handleNavigate = (_accountIndex: number, sectionIndex: number) => {
    if (sectionIndex === -1) {
      setSelectedSectionId("overall");
      return;
    }
    const nextId = navigableSectionIds[sectionIndex];
    if (nextId) setSelectedSectionId(nextId);
  };

  return (
    <>
      <SynopsisSectionCard
        icon={Globe2}
        title={PUBLIC_DATA_SECTION_TITLE}
        titleInfo={<SynopsisLegendInfo label={PUBLIC_DATA_SECTION_TITLE} />}
        iconTone="teal"
        linkLabel="Always On Intelligence"
        onLinkClick={onOpenChannel}
        headerExtra={
          accountId ? (
            <button
              type="button"
              onClick={() => {
                setBriefingView("situation");
                setShowSituationCard(true);
              }}
              aria-haspopup="dialog"
              title={SITUATION_CARDS_LABEL}
              aria-label={SITUATION_CARDS_LABEL}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 text-xs font-medium leading-none text-[#7C3AED] transition-colors hover:underline"
            >
              {/* Same control as the Temp Public Data matrix's row action, so
                  the two entry points to the Situation Card look alike. */}
              <SquarePlay size={16} aria-hidden />
              <span>{SITUATION_CARDS_LABEL}</span>
            </button>
          ) : null
        }
      >
        {accountId ? (
          <XFactorSectionDots
            accountId={accountId}
            accountName={organizationName}
            fallback={
              <div className="py-10 text-center">
                <p className="m-0 text-sm font-medium text-gray-700">
                  No {CHANNEL_LINK_LABEL} for this account
                </p>
                <button
                  type="button"
                  className="mt-4 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm font-medium leading-none text-[#7C3AED] hover:underline"
                  onClick={onOpenChannel}
                >
                  <ArrowRight
                    size={14}
                    strokeWidth={2.5}
                    className="shrink-0"
                    aria-hidden
                  />
                  <span>Open {CHANNEL_LINK_LABEL}</span>
                </button>
              </div>
            }
          />
        ) : null}
      </SynopsisSectionCard>

      <Modal
        open={showSituationCard}
        onClose={() => setShowSituationCard(false)}
        width="max-w-5xl"
        title={
          <span>
            {briefingView === "situation"
              ? "Situation Card"
              : "Executive Brief"}{" "}
            —{" "}
            <span style={{ color: "#ad46ff" }}>
              {organizationName || "Account"}
            </span>
          </span>
        }
        subtitle={modalDate}
        actions={
          <button
            type="button"
            onClick={() =>
              setBriefingView(v => (v === "situation" ? "brief" : "situation"))
            }
            className="inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#ad46ff" }}
          >
            {briefingView === "situation" ? (
              <>
                Executive Brief <ArrowRight size={13} aria-hidden />
              </>
            ) : (
              <>
                <ArrowLeft size={13} aria-hidden /> Situation Card
              </>
            )}
          </button>
        }
        subheader={
          briefingView === "brief" ? <ExecutiveBriefSectionNav /> : undefined
        }
      >
        {accountId ? (
          <XFactorAccountBriefing
            accountId={accountId}
            accountName={organizationName}
            view={briefingView}
            onViewChange={setBriefingView}
            hideChrome
            hideSectionNav
          />
        ) : null}
      </Modal>

      <SectionDetailModal
        isOpen={Boolean(selectedSectionId && briefingAccount)}
        onClose={() => setSelectedSectionId(null)}
        accountData={briefingAccount}
        sectionId={selectedSectionId}
        organizationName={organizationName || undefined}
        allAccounts={briefingAccount ? [briefingAccount] : []}
        allSections={navigableSectionIds}
        currentAccountIndex={0}
        currentSectionIndex={currentSectionIndex}
        onNavigate={handleNavigate}
      />
    </>
  );
};

export default PublicDataSection;
